/**
 * PIN unlock — 5 wrong attempts → full password; forgot PIN → password + new PIN setup.
 * For internal-random accounts (no user password): forgot PIN → reset wallet only.
 */

import { useState } from 'react'
import { PIN_LENGTH } from '../components/PinInput'
import PinInput from '../components/PinInput'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import Logo from '../components/Logo'

const MAX_PIN_ATTEMPTS = 5

export default function PinScreen() {
  const { t } = useTranslation()
  const {
    verifyPin,
    unlockWithPin,
    unlockWithPassword,
    navigate,
    activeAccount,
    setPendingLegacyWalletPassword,
    resetWallet,
  } = useWalletStore()

  const [pin, setPin] = useState('')
  const [attempts, setAttempts] = useState(0)
  const [mode, setMode] = useState<'pin' | 'password' | 'forgot-password' | 'forgot-no-recovery'>('pin')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [resetConfirm, setResetConfirm] = useState(false)

  const left = Math.max(0, MAX_PIN_ATTEMPTS - attempts)

  const onPinChange = (v: string) => {
    setError('')
    setPin(v)
    if (v.length === PIN_LENGTH) {
      void tryPin(v)
    }
  }

  const tryPin = async (code: string) => {
    setBusy(true)
    try {
      const ok = await verifyPin(code)
      if (!ok) {
        setAttempts(a => a + 1)
        setPin('')
        setError(t('pin.wrong'))
        return
      }
      await unlockWithPin(code)
      navigate('wallet')
    } catch (e) {
      setAttempts(a => a + 1)
      setPin('')
      setError(e instanceof Error ? e.message : t('pin.wrong'))
    } finally {
      setBusy(false)
    }
  }

  const onForgotPin = () => {
    setError('')
    setPin('')
    setAttempts(0)
    // internal-random accounts have no user-facing password — only reset option
    const isInternalRandom = activeAccount?.keyEncryption === 'internal-random'
    setMode(isInternalRandom ? 'forgot-no-recovery' : 'forgot-password')
  }

  const submitPassword = async () => {
    const name = activeAccount?.name
    if (!name || !password.trim()) {
      setError(t('common.error'))
      return
    }
    setBusy(true)
    setError('')
    try {
      await unlockWithPassword(name, password)
      if (mode === 'forgot-password') {
        setPendingLegacyWalletPassword(password)
        setPassword('')
        navigate('setup-pin')
      } else {
        setPassword('')
        navigate('wallet')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('pin.wrong'))
    } finally {
      setBusy(false)
    }
  }

  const handleResetWallet = async () => {
    setBusy(true)
    try {
      await resetWallet()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
      setBusy(false)
    }
  }

  // ── Forgot PIN: no recovery (internal-random) ─────────────────────────────
  if (mode === 'forgot-no-recovery') {
    return (
      <div
        className="flex flex-col h-full pt-safe px-4 pb-8 justify-center"
        style={{ background: '#000000' }}
      >
        <div className="card max-w-md mx-auto w-full space-y-4">
          <h2 className="text-xl font-semibold text-white text-center">{t('pin.forgotPin')}</h2>
          <p className="text-sm text-gray-400 text-center leading-relaxed">
            {t('pin.noPasswordRecovery')}
          </p>
          {error ? <p className="text-danger text-sm text-center">{error}</p> : null}
          {!resetConfirm ? (
            <>
              <button
                type="button"
                className="btn-primary w-full"
                style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
                onClick={() => setResetConfirm(true)}
              >
                {t('settings.resetWallet')}
              </button>
              <button
                type="button"
                className="text-sm text-gray-400 w-full text-center mt-2"
                onClick={() => setMode('pin')}
              >
                ← {t('common.back')}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-danger text-center font-medium">
                {t('settings.resetWarning')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  disabled={busy}
                  onClick={() => setResetConfirm(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn-primary flex-1"
                  style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
                  disabled={busy}
                  onClick={() => void handleResetWallet()}
                >
                  {t('settings.confirmReset')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Password entry (legacy accounts) ─────────────────────────────────────
  if (mode === 'password' || mode === 'forgot-password') {
    return (
      <div
        className="flex flex-col h-full pt-safe px-4 pb-8 justify-center"
        style={{ background: '#000000' }}
      >
        <div className="card max-w-md mx-auto w-full space-y-1">
          <h2 className="text-xl font-semibold text-white text-center mb-1">
            {mode === 'forgot-password' ? t('pin.forgotPin') : t('pin.usePassword')}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-4">{t('onboarding.setPassword')}</p>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input-field mb-3"
            autoComplete="current-password"
          />
          {error ? <p className="text-danger text-sm mb-3 text-center">{error}</p> : null}
          <button
            type="button"
            className="btn-primary w-full"
            disabled={busy}
            onClick={() => void submitPassword()}
          >
            {t('common.confirm')}
          </button>
          {mode === 'password' && (
            <button
              type="button"
              className="mt-4 text-sm text-gray-400 w-full text-center"
              onClick={() => {
                setMode('pin')
                setPassword('')
                setError('')
              }}
            >
              {t('common.back')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── PIN entry ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full pt-safe px-4 pb-8" style={{ background: '#000000' }}>
      <div className="flex-1 flex flex-col justify-center">
        <div className="glow-bg flex flex-col items-center mb-2">
          <Logo size="md" className="mb-4" />
        </div>
        <h2 className="text-xl font-semibold text-white text-center mb-2">{t('pin.enter')}</h2>
        {attempts > 0 && (
          <p className="text-sm text-gray-500 text-center mb-6">
            {t('pin.attemptsLeft')} ({left})
          </p>
        )}
        {attempts === 0 && <div className="mb-6" />}

        <PinInput value={pin} onChange={onPinChange} disabled={busy} />

        {error ? (
          <p className="text-danger text-sm text-center mt-6">{error}</p>
        ) : null}

        {attempts >= MAX_PIN_ATTEMPTS && (
          <button
            type="button"
            className="btn-secondary w-full mt-8"
            onClick={() => {
              setMode('password')
              setPin('')
              setError('')
            }}
          >
            {t('pin.usePassword')}
          </button>
        )}

        <button
          type="button"
          className="mt-6 text-sm text-gray-400 text-center"
          onClick={() => void onForgotPin()}
        >
          {t('pin.forgotPin')}
        </button>
      </div>
    </div>
  )
}
